import { RTK_CACHE_KEY_PARAM } from "metabase/api/api";
import { cardApi } from "metabase/api/card";
import { dashboardApi } from "metabase/api/dashboard";
import { datasetApi } from "metabase/api/dataset";
import type { Dispatch } from "metabase/redux/store";
import type Question from "metabase-lib/v1/Question";
import { normalizeParameters } from "metabase-lib/v1/parameters/utils/parameter-values";
import { getPivotOptions } from "metabase-lib/v1/queries/utils/pivot-options";
import type {
  CardQueryRequest,
  DashboardCardQueryRequest,
  Dataset,
  DatasetQuery,
} from "metabase-types/api";

import {
  dispatchQueryEndpoint,
  makePivotAwareQueryRunner,
  shouldUsePivotEndpoint,
} from "./api/query-endpoints";

type RunQuestionQueryOptions = {
  dispatch: Dispatch;
  signal?: AbortSignal;
  isDirty?: boolean;
  token?: string | null;
  ignoreCache?: boolean;
  // Ability to override or add extra query params to the request, used by Embedding SDK
  queryParamsOverride?: Record<string, unknown>;
};

type SavedCardQueryOptions = {
  parameters: unknown[];
  ignoreCache?: boolean;
  token?: string | null;
  queryParamsOverride?: Record<string, unknown>;
};

/**
 * Handles API errors for query endpoints. For 4xx errors from streaming query
 * endpoints, the error response body contains the actual error data that should
 * be displayed (just like the old 202-with-error-in-body behavior). This function
 * converts 4xx errors into successful responses with the error data.
 */
async function handleQueryApiError(
  apiPromise: Promise<Dataset>,
): Promise<Dataset> {
  try {
    return await apiPromise;
  } catch (error) {
    // For 4xx errors, treat the error response body as a successful response
    // (maintaining compatibility with the old 202-with-error-in-body behavior)
    if (
      error != null &&
      typeof error === "object" &&
      "status" in error &&
      typeof error.status === "number" &&
      error.status >= 400 &&
      error.status < 500 &&
      "data" in error &&
      error.data
    ) {
      // The QP returns a structured `{ error, error_type, ... }` body, but
      // plainer endpoints (e.g. `/api/embed/*` API-level checks) return a
      // plain-text body. Normalize so callers can rely on a `{ error, ... }`
      // shape and don't fall through to the empty state (EMB-1659).
      if (typeof error.data === "string") {
        // Unjustified type cast. FIXME
        return {
          error: error.data,
          status: error.status,
        } as unknown as Dataset;
      }
      // Unjustified type cast. FIXME
      return error.data as Dataset;
    }
    // For 5xx and other errors, re-throw
    throw error;
  }
}

// Dispatches the RTK `datasetApi` ad-hoc query endpoint (pivot or non-pivot).
let adhocDatasetQueryCounter = 0;
export function runAdhocDatasetQuery(
  dispatch: Dispatch,
  question: Question,
  body: DatasetQuery & { parameters?: unknown[]; ignore_cache?: boolean },
  signal?: AbortSignal,
): Promise<Dataset> {
  const isPivot = shouldUsePivotEndpoint(question);
  // Disambiguate the RTK cache key so two callers running the same MBQL
  // query get independent cache entries and abort signals. Without this,
  // one caller cancelling would abort the shared in-flight request for
  // every co-subscribed caller. The key is stripped in `baseQuery` before
  // the request hits the server.
  const requestBody = {
    ...(isPivot ? { ...body, ...getPivotOptions(question) } : body),
    [RTK_CACHE_KEY_PARAM]: ++adhocDatasetQueryCounter,
  };
  const endpoint = isPivot
    ? datasetApi.endpoints.getAdhocPivotQuery
    : datasetApi.endpoints.getAdhocQuery;

  return dispatchQueryEndpoint(dispatch, endpoint, requestBody, signal);
}

// Dispatches the RTK saved-card query endpoint, picking the card vs. dashcard
// route (and their pivot variants). Guest embeds rely on the `onBeforeRequest`
// middleware (which RTK requests pass through) rewriting the card route to
// `/api/embed/card/:token/query` when `token` is in the body.
let savedCardQueryCounter = 0;
function runSavedCardQuery(
  dispatch: Dispatch,
  question: Question,
  {
    parameters,
    ignoreCache,
    token,
    queryParamsOverride,
  }: SavedCardQueryOptions,
  signal?: AbortSignal,
): Promise<Dataset> {
  const { dashboardId, dashcardId } = question.getDashboardProps();
  const runQuery = makePivotAwareQueryRunner(dispatch, signal);

  const body = {
    ignore_cache: ignoreCache,
    parameters,
    // Disambiguate the RTK cache key so two callers running the same saved card
    // don't co-subscribe to one in-flight request — otherwise one caller
    // aborting its query (e.g. the SDK cancelling the previous run on every
    // re-run) aborts every co-subscriber's query too. The key is stripped in
    // `baseQuery` before the request hits the server. Mirrors
    // `runAdhocDatasetQuery`.
    [RTK_CACHE_KEY_PARAM]: ++savedCardQueryCounter,
    // `token` and `cardId` identify the card in mutually exclusive ways, so we
    // send only one. Guest and embedded requests carry a `token`: the
    // `onBeforeRequest` middleware rewrites the request to the matching
    // `/api/embed/...` route, discarding
    // the `:cardId` path segment. Authenticated requests carry a `cardId`.
    // The casts below are needed because the shared request types require
    // `cardId`; we keep that contract strict for every other (authenticated)
    // caller and override it only here, on the guest-embed path.
    ...(token ? { token } : { cardId: question.id() }),
    ...queryParamsOverride,
  };

  if (dashboardId != null && dashcardId != null) {
    return runQuery(
      dashboardApi.endpoints.getDashboardCardQuery,
      question,
      // Unjustified type cast. FIXME
      {
        dashboardId,
        dashcardId,
        ...body,
      } as DashboardCardQueryRequest,
    );
  }

  return runQuery(
    cardApi.endpoints.getCardQuery,
    question,
    // Unjustified type cast. FIXME
    body as CardQueryRequest,
  );
}

export async function runQuestionQuery(
  question: Question,
  {
    dispatch,
    signal,
    isDirty = false,
    token,
    ignoreCache = false,
    queryParamsOverride = {},
  }: RunQuestionQueryOptions,
): Promise<[Dataset]> {
  const canUseCardApiEndpoint = !isDirty && question.isSaved();
  const parameters = normalizeParameters(question.parameters());

  if (canUseCardApiEndpoint) {
    return [
      await handleQueryApiError(
        runSavedCardQuery(
          dispatch,
          question,
          {
            parameters,
            ignoreCache,
            token,
            queryParamsOverride,
          },
          signal,
        ),
      ),
    ];
  }

  return [
    await handleQueryApiError(
      runAdhocDatasetQuery(
        dispatch,
        question,
        { ...question.datasetQuery(), parameters },
        signal,
      ),
    ),
  ];
}
